import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ChatBotStateService } from 'src/app/layout/components/topbar/profile/profile-menu/chat-bot/chat-bot-api/chat-bot-state.service';
import { SocketHandlerMethods } from '../enums/socket-handler-methods.enum';
import { SocketHandlerResponseTypes } from '../enums/socket-handler-response-types.enum';
import { AppNotificationService } from './app-notification.service';
import { AuthTokenService } from './authentication/auth-token.service';
import { AppConfigService } from './config.service';
import { ConsoleService } from './console.service';
import { UtilService } from './util.service';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  //#region properties
  appNotificationService = inject(AppNotificationService);
  chatBotStateService = inject(ChatBotStateService);
  titleService = inject(Title);

  private socket: WebSocket;
  private reconnectInterval = 5000; // 20 seconds
  private isManuallyClosed = false;
  //#endregion

  // Connect to the WebSocket server
  connect(): void {
    // Use token as query parameter, encoding it safely
    const url = `${
      AppConfigService.config.notificationSocketUrl
    }?token=Bearer ${AuthTokenService.getAccessToken()}`;

    this.socket = new WebSocket(url);

    // Connection opened
    this.socket.onopen = () => {
      ConsoleService.info('[WebSocket] Connection opened');
    };

    // Receiving message from server
    this.socket.onmessage = (event) => {
      let data: SocketResponse = JSON.parse(event.data);
      data = UtilService.keysToCamelCase(data);

      switch (data.type) {
        case SocketHandlerResponseTypes.NewChatBotMessage:
          this.chatBotStateService.handleNewChatMessage(data.data);
          break;
        case SocketHandlerResponseTypes.NewChatTitle:
          this.chatBotStateService.updateChatTitle(data.data);
          break;
        case SocketHandlerResponseTypes.NewNotification:
          this.appNotificationService.handleNewNotification(data.data);
          break;
      }
    };

    // Connection closed
    this.socket.onclose = (event) => {
      ConsoleService.warn('[WebSocket] Connection closed:', event);

      if (!this.isManuallyClosed) {
        this.reconnect();
      }
    };

    // Handling socket error
    this.socket.onerror = (error) => {
      ConsoleService.error('[WebSocket] Error:', error);
      this.socket.close(); // triggers onclose -> reconnect
    };
  }

  // Send messages to the WebSocket server
  send(message: { type?: SocketHandlerMethods; extraData?: any }): void {
    const body = {
      type: message.type,
      extraData: JSON.stringify(message.extraData),
    };
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(body));
    } else {
      ConsoleService.warn('[WebSocket] Cannot send message, socket not open');
    }
  }

  // Close the WebSocket connection manually
  close(): void {
    this.isManuallyClosed = true;
    this.socket?.close();
  }

  // Reconnect after the connection is closed
  private reconnect() {
    ConsoleService.info(
      `[WebSocket] Attempting to reconnect in ${
        this.reconnectInterval / 1000
      }s...`,
    );

    setTimeout(() => {
      this.connect();
    }, this.reconnectInterval);
  }
}

export interface SocketResponse {
  type?: SocketHandlerResponseTypes;
  data?: object;
  message?: string;
}
